/**
 * Bell — notification bell with unread-count badge, mounted in the header.
 *
 * Opening the panel is read-only. The badge counts the same not-read rows that
 * render red dots and only clears as those rows are explicitly marked read.
 *
 * The bell reads Plan A's JSON HTTP surface. No in-process Reader dependency.
 * Partner-generic: works for agent or LO.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNotifications } from "../../hooks/useNotifications";
import { NotificationPanel } from "./NotificationPanel";

export function Bell() {
  const [open, setOpen] = useState(false);
  const notifs = useNotifications();
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => setOpen(false), []);

  // Close panel when clicking outside.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        close();
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, close]);

  function toggle() {
    setOpen((prev) => !prev);
  }

  const { unreadCount } = notifs;
  const hasBadge = unreadCount > 0;
  const badgeLabel = unreadCount > 99 ? "99+" : String(unreadCount);

  return (
    <div className="notif-bell-wrap">
      <button
        ref={buttonRef}
        type="button"
        aria-label={
          hasBadge
            ? `${unreadCount} unread notification${unreadCount !== 1 ? "s" : ""}`
            : "Notifications"
        }
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={toggle}
        className={[
          "relative inline-flex size-[38px] items-center justify-center rounded-full text-white/80 outline-none transition-colors hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-white/30",
          open ? "bg-white/10 text-white" : "",
        ].join(" ")}
      >
        {/* Bell icon — inline SVG for clean sizing */}
        <BellIcon aria-hidden />

        {/* Unread badge — visible when unreadCount > 0 */}
        {hasBadge && (
          <span className="notif-badge" aria-hidden>
            {badgeLabel}
          </span>
        )}
      </button>

      {/* Panel — positioned absolute below the bell button */}
      {open && (
        <div ref={panelRef} className="notif-panel-positioner">
          <NotificationPanel notifs={notifs} onClose={close} />
        </div>
      )}
    </div>
  );
}

function BellIcon({ "aria-hidden": ariaHidden }: { "aria-hidden"?: true }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={ariaHidden}
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}
